import * as React from 'react';
import { Box, SvgIcon, TablePagination, styled } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';
import Input from 'src/frontend-common/components/Input';
import SearchIcon from '@mui/icons-material/Search';
import { numberKeys } from 'src/utils/record';
import { GetUsersAdminParams } from 'src/dto/User';
import { GetListLimit } from 'src/dto/Limits';
import { useGetUsersAdminQuery } from 'src/redux/services/usersAdmin';
import { UsersList } from '../UsersList';

interface State {
  page: number;
}

export interface Props {
  searchFilter: string | undefined;
  setSearchFilter: React.Dispatch<React.SetStateAction<string | undefined>>;
  limit: GetListLimit;
  setLimit: (i: GetListLimit) => void;
  page?: number;
  setPage?: (s: number) => void;
  teamVersion?: boolean;
}

const limitOptions: Record<GetListLimit, null> = {
  5: null,
  10: null,
  25: null,
  100: null,
};
const sortedLimitOptions = numberKeys(limitOptions).sort((a, b) => a - b);

const pageInitialValue = 0;

const Header = styled(Box)(() => ({
  display: 'flex',
  padding: '16px',
  justifyContent: 'space-between',
}));

const SearchIconWrapper = styled(SvgIcon)(({ theme }) => ({
  marginRight: theme.spacing(1),
}));

const SearchInput = styled(Input)(() => ({
  width: '100%',
  maxWidth: 'none !important',
}));

export const UsersListWrapper: React.FC<Props> = ({
  searchFilter,
  setSearchFilter,
  limit,
  setLimit,
  page: controlledPage,
  setPage: controlledSetPage,
  teamVersion,
}) => {
  const [search, setSearch] = React.useState('');
  const [statePage, stateSetPage] = React.useState<State['page']>(pageInitialValue);
  const [page, setPage] = controlledPage !== undefined && controlledSetPage !== undefined ? [controlledPage, controlledSetPage] : [statePage, stateSetPage];

  const [filters, setFilters] = React.useState<GetUsersAdminParams>({
    start: pageInitialValue,
    max_items: limit,
    search: searchFilter,
  });

  const { data, isLoading, isError } = useGetUsersAdminQuery(filters, {
    skip: !filters,
  });

  const callback = (page: State['page'], limit: Props['limit'], search: string | undefined) => {
    const filters =
      search !== undefined && search !== ''
        ? {
            start: page * limit,
            max_items: limit,
            search,
          }
        : {
            start: page * limit,
            max_items: limit,
            search,
          };

    setFilters(filters);
  };

  const debouncedCallback = useDebouncedCallback(callback, 300);

  React.useEffect(() => {
    setPage(pageInitialValue);

    debouncedCallback.callback(pageInitialValue, limit, searchFilter);
  }, [searchFilter]);

  React.useEffect(() => {
    debouncedCallback.callback(page, limit, searchFilter);
  }, [page, limit]);

  return (
    <Box>
      {!teamVersion && (
        <Header>
          <SearchInput
            disabled={!data}
            id="search-input"
            placeholder="Search by name"
            startAdornment={
              <SearchIconWrapper color="disabled">
                <SearchIcon />
              </SearchIconWrapper>
            }
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                setSearchFilter(search);
              }
            }}
            onChange={(v) => {
              setSearch(v);
            }}
            value={search}
          />
        </Header>
      )}

      {isLoading && <UsersList state="loading" />}
      {isError && <UsersList state="error" />}
      {data && (
        <>
          <div data-testid="loaded-list" />
          <UsersList state="ready" users={data.items} teamVersion={teamVersion} />
        </>
      )}

      <TablePagination
        component="div"
        count={data ? data.total : 1}
        page={page}
        /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
        onPageChange={(_, p) => {
          setPage(p);
        }}
        rowsPerPageOptions={sortedLimitOptions}
        rowsPerPage={limit}
        onRowsPerPageChange={(l) => {
          setPage(pageInitialValue);
          setLimit(parseInt(l.target.value, 10) as GetListLimit);
        }}
      />
    </Box>
  );
};
